import type { Env0Theme } from 'constants/themes.constants';
import { stage } from 'constants/config';
import { thoughtspotPalette } from 'constants/thoughtspot-palette';
const baseUrl = stage === 'prod' ? 'https://app.env0.com' : `https://${stage}.dev.env0.com`;

/**
 * Creates a mapping of ThoughtSpot CSS variables to Env0Theme values
 */
export const getThoughtspotCustomizations = (theme: Env0Theme) => {
  const palette = thoughtspotPalette(theme);

  const variables = {
    // Root and application-wide variables
    '--ts-var-root-color': palette.rootText,
    '--ts-var-root-background': palette.pageSurface,
    '--ts-var-root-font-family': palette.fontFamily,
    '--ts-var-root-text-transform': 'none',
    '--ts-var-application-color': palette.bodyText,
    '--ts-var-root-secondary-color': palette.bodyText,

    // Nav panel
    '--ts-var-nav-color': palette.headingText,
    '--ts-var-nav-background': palette.elementSurface,

    // Buttons
    '--ts-var-button-border-radius': palette.radius,
    '--ts-var-button--icon-border-radius': palette.radius,

    // Primary buttons
    '--ts-var-button--primary-color': palette.onAction,
    '--ts-var-button--primary-background': palette.action,
    '--ts-var-button--primary--hover-background': palette.actionHover,
    '--ts-var-button--primary--font-family': 'inherit',
    '--ts-var-button--primary--active-background': palette.actionActive,

    // Secondary buttons
    '--ts-var-button--secondary-color': palette.bodyText,
    '--ts-var-button--secondary-background': palette.elementSurface,
    '--ts-var-button--secondary--hover-background': palette.hoverSurface,
    '--ts-var-button--secondary--font-family': 'inherit',
    '--ts-var-button--secondary--active-background': palette.activeSurface,

    // Tertiary buttons
    '--ts-var-button--tertiary-color': palette.bodyText,
    '--ts-var-button--tertiary-background': 'transparent',
    '--ts-var-button--tertiary--hover-background': palette.hoverSurface,
    '--ts-var-button--tertiary--active-background': palette.activeSurface,

    // Checkboxes
    '--ts-var-checkbox-error-border': palette.errorBorder,
    '--ts-var-checkbox-border-color': palette.controlBorder,
    '--ts-var-checkbox-hover-border': palette.controlAccent,
    '--ts-var-checkbox-active-color': palette.controlAccent,
    '--ts-var-checkbox-checked-color': palette.controlAccent,
    '--ts-var-checkbox-checked-disabled': palette.disabledSurface,
    '--ts-var-checkbox-highlighted-hover-color': palette.hoverSurface,
    '--ts-var-checkbox-background-color': palette.cardSurface,

    // Menu components
    '--ts-var-menu-color': palette.bodyText,
    '--ts-var-menu-background': palette.cardSurface,
    '--ts-var-menu-font-family': 'inherit',
    '--ts-var-menu-text-transform': 'none',
    '--ts-var-menu--hover-background': palette.hoverSurface,
    '--ts-var-menu-seperator-background': palette.separator,
    '--ts-var-menu-selected-text-color': palette.selectionText,

    // Dialogs and modals
    '--ts-var-dialog-body-background': palette.cardSurface,
    '--ts-var-dialog-body-color': palette.bodyText,
    '--ts-var-dialog-header-background': palette.cardSurface,
    '--ts-var-dialog-header-color': palette.bodyText,
    '--ts-var-dialog-footer-background': palette.cardSurface,

    // Segment control
    '--ts-var-segment-control-hover-background': palette.hoverSurface,

    // Lists
    '--ts-var-list-selected-background': palette.selectionSurface,
    '--ts-var-list-hover-background': palette.hoverSurface,

    // Liveboard
    '--ts-var-liveboard-edit-bar-background': palette.elementSurface,
    '--ts-var-liveboard-cross-filter-layout-background': palette.cardSurface,

    // Visualizations and Answers
    '--ts-var-viz-title-color': palette.bodyText,
    '--ts-var-viz-title-font-family': 'inherit',
    '--ts-var-viz-title-text-transform': 'none',
    '--ts-var-viz-description-color': palette.mutedText,
    '--ts-var-viz-description-font-family': 'inherit',
    '--ts-var-viz-description-text-transform': 'none',
    '--ts-var-viz-border-radius': palette.radius,
    '--ts-var-viz-box-shadow': palette.shadow,
    '--ts-var-viz-background': palette.cardSurface,
    '--ts-var-viz-legend-hover-background': palette.hoverSurface,

    // Filter chips
    '--ts-var-chip-border-radius': palette.radius,
    '--ts-var-chip-title-font-family': 'inherit',
    '--ts-var-chip-box-shadow': palette.shadow,
    '--ts-var-chip-background': palette.pageSurface,
    '--ts-var-chip-color': palette.headingText,
    '--ts-var-chip--hover-background': palette.chipHoverSurface,
    '--ts-var-chip--hover-color': palette.headingText,

    // Axis titles and labels
    '--ts-var-axis-title-color': palette.bodyText,
    '--ts-var-axis-title-font-family': 'inherit',
    '--ts-var-axis-data-label-color': palette.bodyText,
    '--ts-var-axis-data-label-font-family': 'inherit',

    // Chart selection widget
    '--ts-var-answer-chart-select-background': palette.elementSurface,
    '--ts-var-answer-chart-hover-background': palette.hoverSurface,

    // Chart switcher
    '--ts-var-answer-view-table-chart-switcher-active-background': palette.selectionSurface,
    '--ts-var-answer-edit-panel-background-color': palette.elementSurface,

    // Spotter interface
    '--ts-var-spotter-input-background': palette.cardSurface,
    '--ts-var-spotter-prompt-background': palette.elementSurface,

    // Search bar and auto-suggestion panels
    '--ts-var-search-data-button-font-color': palette.bodyText,
    '--ts-var-search-data-button-background': palette.elementSurface,
    '--ts-var-search-data-button-font-family': 'inherit',
    '--ts-var-search-bar-text-font-color': palette.bodyText,
    '--ts-var-search-bar-text-font-family': 'inherit',
    '--ts-var-search-bar-text-font-style': 'normal',
    '--ts-var-search-bar-background': palette.cardSurface,
    '--ts-var-search-auto-complete-background': palette.cardSurface,
    '--ts-var-search-auto-complete-font-color': palette.bodyText,
    '--ts-var-search-auto-complete-subtext-font-color': palette.mutedText,
    '--ts-var-search-navigation-button-background': palette.elementSurface,
    '--ts-var-search-bar-navigation-help-text-background': palette.elementSurface,
    '--ts-var-search-bar-auto-complete-hover-background': palette.hoverSurface,

    // Data and edit panels
    '--ts-var-answer-data-panel-background-color': palette.elementSurface,
    '--ts-var-answer-view-table-chart-switcher-background': palette.elementSurface,

    // Modular Homepage
    '--ts-var-home-watchlist-selected-text-color': palette.selectionText,
    '--ts-var-home-card-color': palette.bodyText,
    '--ts-var-home-favorite-suggestion-card-text-color': palette.bodyText,
    '--ts-var-home-favorite-suggestion-card-text-font-color': palette.bodyText,
    '--ts-var-home-favorite-suggestion-card-background': palette.cardSurface,
    '--ts-var-home-favorite-suggestion-card-icon-color': palette.controlAccent,

    // Natural Language Search panel
    '--ts-var-sage-bar-header-background-color': palette.elementSurface,
    '--ts-var-source-selector-background-color': palette.elementSurface,
    '--ts-var-sage-search-box-font-color': palette.bodyText,
    '--ts-var-sage-search-box-background-color': palette.cardSurface,
    '--ts-var-sage-embed-background-color': palette.cardSurface,
    '--ts-var-sage-seed-questions-background': palette.selectionSurface,
    '--ts-var-sage-seed-questions-font-color': palette.bodyText,
    '--ts-var-sage-seed-questions-hover-background': palette.hoverSurface,
    '--ts-var-source-selector-hover-color': palette.hoverSurface
  };

  const rules_UNSTABLE = {
    '.chat-input-module__chatInputWrapper': {
      // spotter input color
      background: palette.cardSurface,
      color: palette.rootText
    },

    '[data-testid="conv-assist-chat-input"]': {
      color: `${palette.rootText} !important`
    },
    '[data-testid="sage-error-bar"]': {
      // error alert in answer
      'background-color': `${palette.errorSurface} !important`,
      color: palette.errorText
    },
    '[data-testid="error-message"]': {
      // error alert in spotter
      'background-color': `${palette.errorSurface} !important`,
      color: palette.errorText
    },

    '[data-radiant-v2].icon-module__black': {
      // spotter model icon
      fill: `${palette.rootText} !important`
    },
    '[data-radiant-v2].icon-module__white': {
      // send prompt icon color
      fill: `${palette.mutedIcon} !important`
    },
    '.label-v2 > p': {
      // legend text color
      color: `${palette.rootText} !important`
    },
    '.pinboard-edit-header-module__headerLogo': {
      // hide ts logo in liveboard edit
      visibility: 'hidden'
    },
    '.pinboard-module__pinboardPage ': {
      // shows top border in liveboard
      'padding-top': '10px !important'
    },
    '.chat-input-module__chatInputWrapper.chat-input-module__chatInputWrapperActive': {
      border: `1px solid ${palette.mutedIcon} !important`
    },
    '[data-testid="action-selector-nav-btn"]': {
      // remove Custom Actions button
      display: 'none'
    },
    // show scrollbar in modal
    '.modal-module__modal': {
      'max-height': '30rem'
    }
  };
  const strings = {
    Liveboard: 'Dashboard',
    liveboard: 'dashboard',
    Liveboards: 'Dashboards',
    liveboards: 'dashboards',
    Answer: 'Insight',
    answer: 'insight',
    Answers: 'Insights',
    answers: 'insights',
    Spotter: 'Milo',
    'insight questions': 'answer questions'
  };
  const iconSpriteUrl = `${baseUrl}/milo-head.svg`;
  return {
    iconSpriteUrl,
    content: {
      strings
    },
    style: { customCSS: { variables, rules_UNSTABLE } }
  };
};
